import { TagIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { blobEvents } from './definitions/blobEvents';
import { clientCommunicationEvents } from './definitions/clientCommunicationEvents';
import { coverageEvents } from './definitions/coverageEvents';
import { deliverableEvents } from './definitions/deliverableEvents';
import { ledgerEvents } from './definitions/ledgerEvents';
import { matterEvents } from './definitions/matterEvents';
import {
  noteEvents,
  treatmentEvents,
  voiceEvents,
} from './definitions/miscEvents';
import { taskEvents } from './definitions/taskEvents';
import { EventRow, EventText, EventUser } from './shared';
import { EventDefinition, FinchEventCode, RegistryDefinitions } from './types';

// ============================================================================
// EVENT DEFINITIONS AGGREGATION
// ============================================================================

const definitions: RegistryDefinitions = {
  ...matterEvents,
  ...taskEvents,
  ...deliverableEvents,
  ...coverageEvents,
  ...noteEvents,
  ...treatmentEvents,
  ...voiceEvents,
  ...blobEvents,
  ...ledgerEvents,
  // Client communication events - types may not be in generated schema yet
  ...(clientCommunicationEvents as unknown as RegistryDefinitions),
};

// ============================================================================
// FALLBACK
// ============================================================================

const fallbackDefinition: EventDefinition = {
  icon: TagIcon,
  iconColor: 'text-gray-400',
  priority: 'low',
  render: (event, inline) => {
    // Safe access for payload properties that might not exist on all events
    const payload = event.payload as Record<string, unknown>;
    const workLogEntry = payload?.work_log_entry;

    if (workLogEntry && typeof workLogEntry === 'string') {
      if (inline) {
        return (
          <EventRow>
            <EventUser event={event} />
            <EventText>
              {event.event_code.replace(/_/g, ' ').toLowerCase()}
            </EventText>
          </EventRow>
        );
      }
      return (
        <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap break-words">
          {workLogEntry}
        </p>
      );
    }

    return (
      <EventRow>
        <EventUser event={event} />
        <EventText>
          {event.event_code.replace(/_/g, ' ').toLowerCase()}
        </EventText>
      </EventRow>
    );
  },
  description: (event) =>
    `performed ${event.event_code.replace(/_/g, ' ').toLowerCase()}`,
};

// ============================================================================
// PUBLIC API
// ============================================================================

export const getEventDefinition = (code: FinchEventCode): EventDefinition => {
  const def = definitions[code];
  return (def as unknown as EventDefinition) || fallbackDefinition;
};
